import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ScanFace, ArrowDown } from "lucide-react";

const lines = ["Ogni testa racconta", "una storia.", "Impara a leggerla."];

const MaskedLine = ({ text, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {text}
    </motion.span>
  </span>
);

export const Hero = ({ config }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} className="relative min-h-screen flex items-center pt-28 pb-16 px-6 sm:px-10 lg:px-16" data-testid="hero-section">
      <div className="absolute top-0 right-0 w-1/2 h-full bg-tealight/40 rounded-bl-[6rem] -z-10" />
      <div className="max-w-7xl mx-auto w-full grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <motion.div style={{ y: yText }} className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.6 }}
            className="text-xs uppercase tracking-[0.25em] font-mono font-semibold text-coral mb-6"
            data-testid="hero-overline"
          >
            Workshop di Osteopatia Pediatrica · 14–15 Novembre 2026 · Taranto
          </motion.p>
          <h1 className="font-serif font-semibold tracking-tight leading-[1.05] text-petrol text-4xl sm:text-5xl lg:text-6xl" data-testid="hero-title">
            {lines.map((l, i) => (
              <MaskedLine key={l} text={l} delay={0.25 + i * 0.14} />
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.7 }}
            className="mt-7 text-base md:text-lg text-slate2 leading-relaxed max-w-xl"
          >
            Approccio integrato alla <strong className="text-petrol">Plagiocefalia</strong> e alla{" "}
            <strong className="text-petrol">Brachicefalia Posizionale</strong>: valutazione, classificazione
            e trattamento delle deformità craniche del neonato secondo le più recenti evidenze scientifiche.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.7 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              href="#iscrizione"
              data-testid="hero-cta-button"
              className="group inline-flex items-center gap-3 bg-coral hover:bg-coralhover text-ivory font-semibold text-sm sm:text-base px-7 py-4 rounded-full transition-colors duration-300 shadow-lg shadow-coral/25"
            >
              Iscriviti al workshop · 244€
              <ArrowDown className="w-4 h-4 transition-transform duration-300 group-hover:translate-y-1" />
            </a>
            {config && config.posti_rimanenti > 0 ? (
              <span data-testid="hero-seats-badge" className="font-mono text-xs uppercase tracking-widest text-coral font-semibold">
                Solo {config.posti_rimanenti} posti disponibili
              </span>
            ) : config ? (
              <span data-testid="hero-seats-badge" className="font-mono text-xs uppercase tracking-widest text-muted2">
                Edizione al completo
              </span>
            ) : null}
          </motion.div>
        </motion.div>

        <motion.div style={{ y: yImg }} className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.94, rotate: 3 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ delay: 0.5, duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="absolute -inset-3 border border-petrol/15 rounded-[2.5rem] rotate-2" />
            <div className="relative overflow-hidden rounded-[2.2rem] shadow-2xl shadow-petrol/20 aspect-[4/5]">
              <img
                src="https://images.unsplash.com/photo-1620875639608-fae423859fd9?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Valutazione pediatrica del neonato"
                className="w-full h-full object-cover"
                data-testid="hero-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-petroldeep/50 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 flex items-center gap-3 backdrop-blur-xl bg-ivory/85 border border-petrol/10 rounded-2xl px-4 py-3">
                <ScanFace className="w-6 h-6 text-coral shrink-0" />
                <p className="text-xs sm:text-sm font-medium text-petrol leading-snug">
                  Supera gli errori del craniometro analogico, impara ad utilizzare la scansione volumetrica cranica digitale
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
